import { concatBytes, latin1Decode } from '@casa/util';

/** A response from the server, or a request the server sent to the client. */
export interface RtspTextMessage {
    kind: 'response' | 'request';
    /** `RTSP/1.0 200 OK` or `OPTIONS rtsp://... RTSP/1.0`. */
    startLine: string;
    /** Responses only; 0 for a request. */
    status: number;
    reason: string;
    /** Header names in lower case. A repeated header has one entry per line. */
    headers: Map<string, string[]>;
    body: Uint8Array;
}

/** A binary block on the RTSP connection (RFC 2326 §10.12) : `$`, channel, 16-bit length, data. */
export interface InterleavedFrame {
    kind: 'interleaved';
    channel: number;
    payload: Uint8Array;
}

export type RtspMessage = RtspTextMessage | InterleavedFrame;

export class RtspParseError extends Error {
    override name = 'RtspParseError';
}

const DOLLAR = 0x24;
const CR = 0x0d;
const LF = 0x0a;
const MAX_HEADER_BYTES = 64 * 1024;
const MAX_BODY_BYTES = 1024 * 1024;

export function header(message: RtspTextMessage, name: string) : string | undefined {
    return message.headers.get(name.toLowerCase())?.[0];
}

function findHeaderEnd(bytes: Uint8Array, from: number) : number {
    for (let i = from; i + 3 < bytes.length; i++) {
        if (bytes[i] === CR && bytes[i + 1] === LF && bytes[i + 2] === CR && bytes[i + 3] === LF) {
            return i;
        }
    }
    return -1;
}

function parseText(head: string, body: Uint8Array) : RtspTextMessage {
    const [startLine = '', ...lines] = head.split('\r\n');
    const headers = new Map<string, string[]>();
    for (const line of lines) {
        const separator = line.indexOf(':');
        if (separator <= 0) {
            continue;
        }

        const name = line.slice(0, separator).trim().toLowerCase();
        const values = headers.get(name) ?? [];
        values.push(line.slice(separator + 1).trim());
        headers.set(name, values);
    }
    const response = /^RTSP\/\d\.\d\s+(\d{3})\s*(.*)$/.exec(startLine);

    return response
        ? { kind: 'response', startLine, status: Number(response[1]), reason: response[2] ?? '', headers, body }
        : { kind: 'request', startLine, status: 0, reason: '', headers, body };
}

/**
 * Splits the byte stream of an RTSP connection into text messages and interleaved binary frames.
 * Chunk boundaries of the transport carry no meaning; a message may arrive in any number of pieces.
 */
export class RtspParser {
    #pending: Uint8Array = new Uint8Array();

    push(chunk: Uint8Array) : RtspMessage[] {
        const bytes = this.#pending.length === 0 ? chunk : concatBytes([this.#pending, chunk]);
        const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
        const messages: RtspMessage[] = [];
        let offset = 0;

        while (offset < bytes.length) {
            const first = bytes[offset]!;
            if (first === DOLLAR) {
                if (bytes.length - offset < 4) {
                    break;
                }

                const end = offset + 4 + view.getUint16(offset + 2);
                if (end > bytes.length) {
                    break;
                }

                messages.push({ kind: 'interleaved', channel: bytes[offset + 1]!, payload: bytes.subarray(offset + 4, end) });
                offset = end;
                continue;
            }
            // Empty lines between messages are allowed.
            if (first === CR || first === LF) {
                offset++;
                continue;
            }
            const headerEnd = findHeaderEnd(bytes, offset);
            if (headerEnd < 0) {
                if (bytes.length - offset > MAX_HEADER_BYTES) {
                    throw new RtspParseError('message header too large');
                }

                break;
            }
            const head = latin1Decode(bytes.subarray(offset, headerEnd));
            if (!/^(RTSP\/\d\.\d \d{3}|[A-Z_]+ \S+ RTSP\/\d\.\d)/.test(head)) {
                throw new RtspParseError('lost synchronisation with the RTSP stream');
            }

            const message = parseText(head, bytes.subarray(0, 0));
            const lengthText = header(message, 'content-length');
            const bodyLength = lengthText === undefined ? 0 : Number(lengthText);
            if (!Number.isInteger(bodyLength) || bodyLength < 0 || bodyLength > MAX_BODY_BYTES) {
                throw new RtspParseError('invalid Content-Length');
            }

            const end = headerEnd + 4 + bodyLength;
            if (end > bytes.length) {
                break;
            }
            // Copied: unlike an interleaved payload, a body is read after the chunk may have been handed on.
            message.body = bytes.slice(headerEnd + 4, end);
            messages.push(message);
            offset = end;
        }

        // Copied, so that a few left-over bytes do not keep a whole transport chunk alive.
        this.#pending = bytes.slice(offset);

        return messages;
    }
}
